import { forwardRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  IconButton,
  Slide,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocaleKeys from '../../../locales/keys';
import { useTranslation } from 'react-i18next';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useUpdateSubscription } from '../../../common/hooks/useUpdateSubscription';
import postMessage from '../../../utils/postMessage';
import useSubscriptionFlowAnalyticsParams from '../../../common/hooks/useSubscriptionFlowAnalyticsParams';

const useStyles = makeStyles((theme) => ({
  topScrollPaper: {
    alignItems: 'flex-start',
  },
  topPaperScrollBody: {
    verticalAlign: 'top',
  },
  dialogModal: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
  },
  paper: {
    boxShadow: '0 5px 15px rgba(0,0,0,.5)',
    border: '1px solid rgba(0,0,0,.2)',
    borderRadius: '6px',
  },
  dialogTitle: {
    padding: '15px',
    [theme.breakpoints.between(0, 'xs')]: {
      flex: 'unset',
    },
  },
  dialogContent: {
    padding: '15px',
    marginBottom: '24px',
  },
  dialogContentText: {
    fontSize: '18px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    lineHeight: '1.1',
  },
  dialogActions: {
    justifyContent: 'space-around',
    padding: '15px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  actionButton: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  renewButton: {
    height: '37px',
    borderRadius: '5px',
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`,
      background: theme.palette.custom.white,
      color: theme.palette.custom.lightBlack,
    },
    [theme.breakpoints.up('md')]: {
      marginRight: '15px',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: '20px',
    },
  },
  cancelButton: {
    background: theme.palette.custom.darkGreyBg,
    borderRadius: '5px',
    color: theme.palette.custom.white,
    [theme.breakpoints.up('md')]: {
      marginLeft: '15px',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0 !important',
    },
    height: '37px',
    '&:hover': {
      background: theme.palette.primary.main,
    },
  },
  closeIcon: {
    fontSize: '21px',
    fontWeight: '700',
    lineHeight: 1,
    textShadow: `0 1px 0 ${theme.palette.custom.white}`,
    padding: '3px 0',
    marginTop: '-30px',
    color: theme.palette.custom.black,
    opacity: '0.2',
  },
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const RenewSubscriptionReasonForm = (props) => {
  const {
    showRenewSubscriptionForm,
    subscriptionCycleDate,
    subId,
    showErrorDialogHandler,
    onUpdate,
    currentPlan,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [
    updateSubscription,
    updateSubscriptionFault,
    requestUpdateSubscription,
  ] = useUpdateSubscription();

  const [open, setOpen] = useState(showRenewSubscriptionForm);

  const [language, ,] = useSubscriptionFlowAnalyticsParams();

  const renewSubscriptionHandler = () => {
    postMessage('module.showLoader');
    requestUpdateSubscription({
      Renew: true,
      Id: subId,
    });
  };

  const sendManageSubscriptionMessage = (updateSubscription) => {
    if (updateSubscription) {
      let splitPlan = currentPlan || ' ';
      splitPlan = splitPlan.split(' ');
      const paymentType = 'Credit Card';
      const event = 'subscriptionManagement';

      const updatedSubscriptionPackage =
        updateSubscription?.Subscription?.Items[0]?.Product?.Name;
      const renewSubscription = {
        event,
        subscriptionPlan: splitPlan[0],
        paymentType: paymentType,
        actionType: 'renew',
        subscriptionType: splitPlan[1],
        subscriptionPackage: updatedSubscriptionPackage,
        selectedLanguage: language,
      };
      postMessage(event, renewSubscription);
    }
  };

  useEffect(() => {
    if (updateSubscription) {
      postMessage('module.hideLoader');

      sendManageSubscriptionMessage(updateSubscription);
      setOpen(false);
      if (onUpdate) {
        postMessage('module.hideBackdrop');
        onUpdate(updateSubscription);
      }
    }
  }, [updateSubscription]);

  useEffect(() => {
    if (updateSubscriptionFault) {
      postMessage('module.hideLoader');
      setOpen(false);
      showErrorDialogHandler(updateSubscriptionFault);
    }
  }, [updateSubscriptionFault]);

  useEffect(() => {
    postMessage('module.showBackdrop');
  }, []);

  const handleClose = () => {
    postMessage('module.hideBackdrop');
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      TransitionComponent={Transition}
      classes={{
        scrollPaper: classes.topScrollPaper,
        paperScrollBody: classes.topPaperScrollBody,
        paper: classes.paper,
      }}
      className={classes.dialogModal}
    >
      <Box display="flex" justifyContent="space-between">
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography variant="h2">
            {t(LocaleKeys.retention.renewSubscriptionTitle)}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          disableRipple
          disableFocusRipple
          variant="text"
          size="small"
        >
          <CloseRoundedIcon className={classes.closeIcon} />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.dialogContentText}>
          {t(LocaleKeys.retention.renewSubscriptionContent, {
            subscriptionCycleDate,
          })}
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions classes={{ root: classes.dialogActions }}>
        <Button
          disableRipple
          disableFocusRipple
          size="small"
          variant="contained"
          classes={{ root: classes.actionButton }}
          className={classes.renewButton}
          color="primary"
          onClick={renewSubscriptionHandler}
        >
          <Typography variant="button">
            {t(LocaleKeys.retention.renewSubscriptionFormRenewButton)}
          </Typography>
        </Button>
        <Button
          disableRipple
          disableFocusRipple
          size="small"
          variant="contained"
          classes={{ root: classes.actionButton }}
          className={classes.cancelButton}
          onClick={handleClose}
        >
          <Typography variant="button">
            {t(LocaleKeys.retention.renewSubscriptionFormCancelButton)}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewSubscriptionReasonForm;
